import { deletePushSubscription, savePushSubscription } from '../api/push';
import { SETTINGS } from '../settings';
import { isInstalled, isIosDevice } from './utils';

export type PushSupport = 'supported' | 'ios-needs-install' | 'unsupported' | 'denied';

export const getPushSupport = (): PushSupport => {
  if (typeof window === 'undefined') {
    return 'unsupported';
  }
  if (isIosDevice() && !isInstalled()) {
    return 'ios-needs-install';
  }
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return 'unsupported';
  }
  if (Notification.permission === 'denied') {
    return 'denied';
  }
  return 'supported';
};

export const urlBase64ToUint8Array = (base64String: string) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export const getPushSubscription = async (): Promise<PushSubscription | null> => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return null;
  }
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) {
      return null;
    }
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
};

export const enablePush = async (): Promise<{ success: boolean; error?: string }> => {
  if (typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator)) {
    return { success: false, error: 'Push notifications are not supported on this browser' };
  }
  if (!SETTINGS.VAPID_PUBLIC_KEY) {
    return { success: false, error: 'Missing VAPID public key (VITE_VAPID_PUBLIC_KEY)' };
  }
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return {
        success: false,
        error: permission === 'denied' ? 'Notification permission was denied' : 'Notification permission was not granted',
      };
    }
    let reg = await navigator.serviceWorker.getRegistration();
    if (!reg) {
      const swUrl = import.meta.env.DEV ? '/dev-sw.js?dev-sw' : '/sw.js';
      reg = await navigator.serviceWorker.register(swUrl, { scope: '/' });
    }
    if (reg?.waiting) {
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    const readyReg = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<ServiceWorkerRegistration>((_, reject) =>
        setTimeout(() => reject(new Error('Service worker is not ready or failed to register.')), 10000),
      ),
    ]);
    let sub = await readyReg.pushManager.getSubscription();
    if (!sub) {
      sub = await readyReg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(SETTINGS.VAPID_PUBLIC_KEY),
      });
    }
    return new Promise<{ success: boolean; error?: string }>(resolve => {
      savePushSubscription(sub!, resp => {
        resolve({
          success: !!resp.success,
          error: resp.success ? undefined : resp.error || 'Failed to save subscription',
        });
      });
    });
  } catch (e) {
    console.error('Error enabling push notifications:', e);
    return { success: false, error: e instanceof Error ? e.message : String(e) };
  }
};

export const disablePush = async (): Promise<boolean> => {
  try {
    const sub = await getPushSubscription();
    if (sub) {
      await new Promise<boolean>(resolve => {
        deletePushSubscription(sub.endpoint, resp => {
          resolve(!!resp.success);
        });
      });
      await sub.unsubscribe();
    }
    return true;
  } catch (e) {
    console.error('Error disabling push notifications:', e);
    return false;
  }
};

export const refreshPushSubscription = async (): Promise<void> => {
  try {
    if (
      getPushSupport() === 'supported' &&
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted'
    ) {
      const sub = await getPushSubscription();
      if (sub) {
        savePushSubscription(sub, () => {});
      }
    }
  } catch {
    // catch & ignore errors
  }
};
